import { NgRedux } from '@angular-redux/store';
import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  Inject,
  ViewChild,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subscription, of } from 'rxjs';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { IAppState } from 'src/app/shared/state-management/state-interfaces';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { convertDateTimeFormat } from 'src/app/shared/utils/utility.service';
import { AccessPointFilterActvityComponent } from '../access-point-filter-actvity/access-point-filter-actvity.component';
import { Activities, IActivityFilterCriteria } from '../activity.interface';
import { ActivityComponent } from '../activity.component';

@Component({
  selector: 'app-view-all-item',
  templateUrl: './view-all-item.component.html',
  styleUrls: ['./view-all-item.component.scss'],
})
export class ViewAllItemComponent implements OnInit {
  @Output() stepperChange = new EventEmitter<number>();
  @ViewChild(ActivityComponent) activityRefComp: ActivityComponent | undefined;
  accessPointActivityPageSize = 15;
  canLoad: boolean = false;
  sharedData: any;
  subscription: Subscription | undefined;
  filterActivity: any;
  title: string | undefined;
  filterCount: number = 0;
  readonly LANG_CONST = LANG_CONST;
  activities!: Observable<Activities>;
  serverActivityCount: number | undefined;
  activityCount: number | undefined;
  type: string | undefined;
  CONSTANTS = CONSTANTS;
  scrollTop: boolean = false;

  filterCriteria: IActivityFilterCriteria = {
    eventCode: [],
    dateTo: null,
    dateFrom: null,
    searchEntity: undefined,
    pagination: {
      cursor: [],
      size: 20,
    },
  };
  cursor: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<ViewAllItemComponent>,
    private ngRedux: NgRedux<IAppState>,
    private floorManagementService: FloorManagementService,
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    @Inject(MAT_DIALOG_DATA) activityData: any
  ) {
    this.filterCount = activityData.filterCount! ?? 0;
    this.filterActivity = activityData.filter!;
    this.title = activityData.title!;
    this.type = activityData.type!;
  }
  ngOnInit(): void {
    this.subscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
      }
    );

    if (this.type === CONSTANTS.UNIT) {
      this.ngRedux.select('unitActivities').subscribe((res: any) => {
        this.activities = of(res);
        this.activityCount = res?.events?.length;
        this.serverActivityCount = res?.totalRecords;
        this.cursor = res?.cursor;
        this.canLoad = false;
      });
    }

    if (this.type === CONSTANTS.ACCESSPOINT) {
      this.ngRedux.select('accessPointActivities').subscribe((res: any) => {
        this.activities = of(res);
        this.activityCount = res?.events?.length;
        this.serverActivityCount = res?.totalRecords;
        this.cursor = res?.cursor;
        this.canLoad = false;
      });
    }

    if (this.filterActivity?.dates?.start) {
      this.filterCriteria.dateFrom = convertDateTimeFormat(
        this.filterActivity?.dates?.start,
        "yyyy-MM-dd'T'HH:mm:ss"
      );
    } else {
      this.filterCriteria.dateFrom = null;
    }

    if (this.filterActivity?.dates?.end) {
      this.filterCriteria.dateTo = convertDateTimeFormat(
        this.filterActivity?.dates?.end,
        "yyyy-MM-dd'T'HH:mm:ss"
      );
    } else {
      this.filterCriteria.dateTo = null;
    }
    this.filterCriteria.eventCode = this.filterActivity?.lockIds || [];
  }

  /**
   * @description close modal by the help of dialgue reference
   * @returns void
   */
  closeDialog(): void {
    const activityData = {
      accessPointActivities: this.activities,
      filterCount: this.filterCount,
      filter: this.filterActivity,
    };
    this.dialogRef?.close(activityData);
  }

  /**
   * @description this function responsible of fetch newly data and store update
   * @param ev event of scroll
   * @returns void
   */
  scrollDownEmitter(ev: any) {
    // prevent multiple calls while data is loading
    if (this.canLoad || this.activityCount === this.serverActivityCount) {
      return;
    }
    
    if (this.cursor === null || this.cursor === undefined || (Array.isArray(this.cursor) && this.cursor.length === 0)) {
      this.canLoad = false;
      return;
    }
    
    this.canLoad = true;
    
    if (this.type === CONSTANTS.ACCESSPOINT) {
      this.filterCriteria.pagination.cursor = this.cursor;
      this.filterCriteria.siteId = this.sharedData?.selectedSite?.siteId;
      this.floorManagementService
        .getEventHistoryByAccessPoint(
          this.sharedData?.selectedAccessPoint?.accessPointId,
          this.filterCriteria,
          true,
          true
        )
        .subscribe({
          next: (res: any) => {
          },
          error: () => {
            this.canLoad = false;
          }
        });
    }
    if (this.type === CONSTANTS.UNIT) {
      this.filterCriteria.pagination.cursor = this.cursor;
      this.filterCriteria.siteId = this.sharedData?.selectedSite?.siteId;
      this.floorManagementService
        .getEventHistoryByUnit(
          this.sharedData?.selectedUnit?.unitId,
          this.filterCriteria,
          true,
          true
        )
        .subscribe({
          next: (res: any) => {
          },
          error: () => {
            this.canLoad = false;
          }
        });
    }
  }

  /**
   * @description #accessPointActivityFilter opens the mofal for filter activities.
   * @returns void
   */
  accessPointActivityFilter() {
    const dialogInstance = this.helperService.openModalDialog(
      AccessPointFilterActvityComponent,
      this.filterActivity,
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((filter: any) => {
      this.activityRefComp?.resetScrollPosition();
      if (filter) {
        this.filterActivity = filter;
        const userCount = this.filterActivity?.users !== '' ? 1 : 0;
        const dateCount = this.filterActivity?.dates ? 1 : 0;
        this.filterCount =
          this.filterActivity?.lockType?.length + userCount + dateCount;

        this.filterCriteria.searchEntity = JSON.stringify({
          user: filter?.users,
        });

        if (filter?.dates?.start && filter?.dates?.end) {
          this.filterCriteria.dateFrom = convertDateTimeFormat(
            filter?.dates?.start,
            "yyyy-MM-dd'T'HH:mm:ss"
          );
          this.filterCriteria.dateTo = convertDateTimeFormat(
            filter?.dates?.end,
            "yyyy-MM-dd'T'HH:mm:ss"
          );
        } else {
          this.filterCriteria.dateFrom = null;
          this.filterCriteria.dateTo = null;
        }

        this.filterCriteria.eventCode = filter?.lockIds;
        if (this.type === CONSTANTS.UNIT) {
          this.getUnitsActivity(filter);
        } else if (this.type === CONSTANTS.ACCESSPOINT) {
          this.getAccessPointActivity(filter);
        }
      } else {
        return;
      }
    });
  }

  /**
   * @description #initializeFilter function gets the floorList from app and
   *               map the data int the required state of filter component.
   * @returns void
   */
  initializeFilter() {
    this.filterActivity = {
      users: '',
      dates: '',
      lockType: [],
    };
  }

  /**
   * @description #getAccessPointActivity function fetch the access points.
   * @returns void
   */
  getAccessPointActivity(filter?: any): void {
    const prepend: boolean = filter?.prepend === false ? false : true;
    if (!filter?.cursor) {
      this.filterCriteria.pagination.cursor = [];
    } else {
      this.filterCriteria.pagination.cursor = this.cursor;
    }
    this.canLoad = true;
    this.filterCriteria.siteId = this.sharedData?.selectedSite?.siteId;
    this.floorManagementService
      .getEventHistoryByAccessPoint(
        this.sharedData?.selectedAccessPoint?.accessPointId,
        this.filterCriteria,
        prepend,
        prepend
      )
      .subscribe({
        next: (res: any) => {
          // Redux subscription will handle updates
          this.filterActivity.entityType = CONSTANTS.ACCESSPOINT;
        },
        error: () => {
          this.canLoad = false;
        }
      });
  }

  /**
   * @description #getUnitsActivity function fetch the units.
   * @returns void
   */
  getUnitsActivity(filter?: any): void {
    const prepend: boolean = filter?.prepend === false ? false : true;
    if (!filter?.cursor) {
      this.filterCriteria.pagination.cursor = [];
    } else {
      this.filterCriteria.pagination.cursor = this.cursor;
    }
    this.canLoad = true;
    this.filterCriteria.siteId = this.sharedData?.selectedSite?.siteId;
    this.floorManagementService
      .getEventHistoryByUnit(
        this.sharedData?.selectedUnit?.unitId,
        this.filterCriteria,
        prepend,
        prepend
      )
      .subscribe({
        next: (res: any) => {
          this.filterActivity.entityType = CONSTANTS.UNIT;
        },
        error: () => {
          this.canLoad = false;
        }
      });
  }
}
